import { useState, useEffect, useCallback, useRef } from 'react';
import RatingIcon from '../shared/RatingIcon';
import { getAvatarData } from '../../utils/avatarUtils';
import { ICON_GROUPS, ICON_DESCRIPTIONS, PILL_COLORS } from '../../utils/iconDescriptions';
import { Tooltip } from '../shared/Tooltip';

function formatPhone(phone) {
  if (!phone) return '';
  const clean = phone.replace(/\D/g, '');
  if (clean.startsWith('569') && clean.length === 11) {
    return `+56 9 ${clean.slice(3, 7)} ${clean.slice(7)}`;
  }
  return phone;
}

function IconGroupRow({ group, children }) {
  if (!group.label) {
    return <div className="flex gap-2 flex-wrap">{children}</div>;
  }
  const colors = PILL_COLORS[group.color] || PILL_COLORS.green;
  return (
    <div className={`rounded-2xl border px-3 py-0.5 ${colors.container}`}>
      <div className={`text-[10px] font-bold uppercase tracking-wider mb-0.5 px-1 ${colors.label}`}>
        {group.label}
      </div>
      <div className="flex gap-2 flex-wrap">{children}</div>
    </div>
  );
}

export default function ICConfirmationModal({
  pending,
  currentIndex,
  totalCount,
  onConfirm,
  loading,
}) {
  const [selectedIcon, setSelectedIcon] = useState(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    setSelectedIcon(null);
  }, [pending?.note_id]);

  useEffect(() => {
    if (!pending) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [pending]);

  useEffect(() => {
    if (pending && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [pending?.note_id]);

  const handleConfirmClick = useCallback(() => {
    if (selectedIcon && !loading) {
      onConfirm(selectedIcon);
    }
  }, [selectedIcon, loading, onConfirm]);

  if (!pending) return null;

  const currentIconName = pending.current_icon || null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ic-confirm-title"
        tabIndex={-1}
        className="bg-white dark:bg-surface-dark rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto focus:outline-none"
      >
        <div className="bg-amber-500 px-6 py-2 rounded-t-2xl flex items-center gap-3">
          <span className="text-2xl" aria-hidden="true">⚠</span>
          <h2 id="ic-confirm-title" className="text-lg font-semibold text-white">
            Calificación pendiente
          </h2>
          {totalCount > 1 && (
            <span className="ml-auto text-xs font-medium text-white bg-amber-600/50 px-2 py-0.5 rounded-full">
              {currentIndex} de {totalCount}
            </span>
          )}
        </div>

        <div className="px-6 pt-1.5 pb-0.5 flex items-center gap-2">
          {(() => {
            const avatar = getAvatarData(pending.contact_name || pending.phone, pending.phone);
            return (
              <div
                className="w-10 h-10 rounded-xl shadow-md flex items-center justify-center text-white font-bold text-base shrink-0"
                style={{ background: avatar.gradient }}
                aria-hidden="true"
              >
                {avatar.initials}
              </div>
            );
          })()}
          <div className="min-w-0">
            <p className="text-base font-medium text-gray-900 dark:text-gray-100 truncate leading-tight">
              {pending.contact_name || pending.phone}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatPhone(pending.phone)}
            </p>
          </div>
        </div>

        <p className="px-6 pb-1.5 text-xs text-gray-600 dark:text-gray-400">
          Vuelve a clickear el IC actual para mantenerlo, o selecciona uno nuevo.
        </p>

        <div className="px-6 pb-2 space-y-1">
          {ICON_GROUPS.map(group => (
            <IconGroupRow key={group.key} group={group}>
              {group.icons.map(iconId => {
                const isSelected = selectedIcon === iconId;
                const isCurrent = currentIconName === iconId;
                const desc = ICON_DESCRIPTIONS[iconId] || { title: iconId, body: '' };
                const ringClass = isSelected
                  ? `ring-2 ring-amber-500 ring-offset-1 scale-110${isCurrent ? ' ic-current-glow' : ''}`
                  : isCurrent
                    ? 'ic-current-glow'
                    : 'ring-1 ring-gray-200 hover:ring-gray-300';
                return (
                  <Tooltip key={iconId} title={desc.title} body={desc.body} delay={300}>
                    <button
                      type="button"
                      onClick={() => setSelectedIcon(iconId)}
                      aria-label={desc.title}
                      className={`w-14 h-14 flex items-center justify-center rounded-lg bg-white dark:bg-surface-dark-alt transition-all duration-150 ${ringClass}`}
                    >
                      <RatingIcon iconId={iconId} size="lg" />
                    </button>
                  </Tooltip>
                );
              })}
            </IconGroupRow>
          ))}
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-surface-dark px-6 py-2 border-t dark:border-surface-dark-border flex justify-end">
          <button
            type="button"
            disabled={!selectedIcon || loading}
            onClick={handleConfirmClick}
            className={
              selectedIcon && !loading
                ? 'bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors'
                : 'bg-gray-300 text-gray-500 px-6 py-2 rounded-lg cursor-not-allowed font-medium'
            }
          >
            {loading ? 'Confirmando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}
